import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button/Button';
import { useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FormProvider, { RHFInput } from 'Components/Organisms/HookForm';
import { useForm } from 'react-hook-form';
import { cancelInvoice } from 'Services/invoices';
import deleteImage from 'assets/icon-delete.svg';

const magicWords = {
    ES: 'CANCELAR',
    EN: 'CANCEL',
};

export default function InvoiceCancelDrawer(props) {
    const { invoice, onCancel, onSubmit, onError } = props;

    const queryClient = useQueryClient();

    const { t, i18n } = useTranslation('invoices');
    const magicWord = magicWords[i18n.language] || magicWords.EN;

    const schema = yup.object().shape({
        captcha: yup
            .string()
            .required()
            .transform((_value, originalValue) => originalValue.toUpperCase().trim())
            .oneOf([magicWord], t('invalid-magic-word', { magicWord })),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            captcha: '',
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const handleCancel = async () => {
        try {
            await cancelInvoice(invoice.id);
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            onSubmit();
        } catch (error) {
            onError(error);
        }
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(handleCancel)} className="flex flex-col h-full">
            <div className="mt-6 mb-6 px-3">
                <div className="text-center">
                    <div className="mb-6 flex justify-center">
                        <img src={deleteImage} alt="" />
                    </div>
                    <div className="text-xl font-semibold mb-4 text-dark-blue text-pretty">
                        {t('cancel-invoice-title')}
                    </div>
                    <div className="text-lg mb-8 text-gray-500 text-pretty">{t('cancel-invoice-description')}</div>
                </div>
                <div className="py-3">
                    <RHFInput data-autofocus="true" label={t('write-magic-word', { magicWord })} name="captcha" />
                </div>
            </div>

            <div className="grow" />

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button
                        isBusy={isSubmitting}
                        variant="primary"
                        className="grow"
                        type="submit"
                        testId="invoice-cancel.cancel-invoice"
                    >
                        {t('cancel-button')}
                    </Button>
                    <Button variant="default" className="grow" onClick={onCancel} testId="invoice-cancel.close">
                        {t('close')}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

InvoiceCancelDrawer.propTypes = {
    invoice: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};
